import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface UserTenant {
  tenant_id: string;
  role: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
}

// Default tenant ID for PureAura Health Care Private Limited
export const DEFAULT_TENANT_ID = 'a0000000-0000-0000-0000-000000000001';

export const useUserTenants = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-tenants", user?.id],
    queryFn: async (): Promise<UserTenant[]> => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("tenant_user_roles")
        .select(`
          tenant_id,
          role,
          tenant:tenants(id, name, slug)
        `)
        .eq("user_id", user.id);

      if (error) throw error;
      
      // Filter out any entries with missing or invalid tenant data
      const validData = (data || []).filter(d => 
        d.tenant !== null && 
        typeof d.tenant === 'object' && 
        'name' in d.tenant && 
        typeof d.tenant.name === 'string'
      ) as UserTenant[];
      
      return validData;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Helper to get just the tenant IDs
export const useUserTenantIds = () => {
  const { data: tenants, isLoading, error } = useUserTenants();
  
  return {
    tenantIds: (tenants || [])
      .filter(t => t.tenant_id)
      .map(t => t.tenant_id),
    tenants: tenants || [],
    isLoading,
    error,
  };
};

// Helper to get the current/active tenant (for single-tenant mode, returns default)
export const useCurrentTenant = () => {
  const { data: tenants, isLoading, error } = useUserTenants();
  
  // For now, return the first tenant or default
  const currentTenant = tenants?.[0] || null;
  const currentTenantId = currentTenant?.tenant_id || DEFAULT_TENANT_ID;
  
  return {
    currentTenant,
    currentTenantId,
    isLoading,
    error,
  };
};
